import React from 'react';
import { Link } from 'react-router-dom';
import { useMessageNotifications } from '../../contexts/MessageNotificationContext';
import { formatRelativeTime } from '../../utils/formatRelativeTime';

const DROPDOWN_ID = 'messagesDropdown';
const MAX_ITEMS = 3;

interface MessageCenterDropdownProps {
  /** Tighter spacing when placed beside notification bell. */
  compact?: boolean;
}

const MessageCenterDropdown: React.FC<MessageCenterDropdownProps> = ({ compact = false }) => {
  const { messageUnreadCount, conversationSummaries, markConversationRead } =
    useMessageNotifications();

  const items = conversationSummaries.slice(0, MAX_ITEMS);

  return (
    <div className={`dropdown position-relative${compact ? '' : ' me-2'}`}>
      <button
        type="button"
        className="sb-header__icon-btn"
        id={DROPDOWN_ID}
        data-bs-toggle="dropdown"
        aria-expanded="false"
        aria-label={messageUnreadCount > 0 ? `Mesajlar (${messageUnreadCount} yeni)` : 'Mesajlar'}
        title="Mesajlar"
      >
        <i className="far fa-envelope" aria-hidden="true" />
        {messageUnreadCount > 0 && (
          <span className="sb-header__dot sb-header__dot--primary" aria-hidden="true" />
        )}
      </button>
      <ul
        className="dropdown-menu dropdown-menu-end p-0 overflow-hidden"
        aria-labelledby={DROPDOWN_ID}
        style={{ minWidth: '320px', maxWidth: '380px' }}
      >
        <li>
          <h6 className="dropdown-header bg-primary text-white m-0 px-3 py-2">Mesajlar</h6>
        </li>
        {items.length === 0 ? (
          <li>
            <div className="px-3 py-3 text-muted small text-center">Henüz mesaj yok.</div>
          </li>
        ) : (
          items.map((s) => (
            <li key={s.conversationId}>
              <Link
                to={`/mesajlar?conversationId=${s.conversationId}`}
                className="dropdown-item px-3 py-2 d-flex flex-column"
                onClick={() => {
                  void markConversationRead(s.conversationId);
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span
                    className="fw-semibold text-truncate"
                    style={{ maxWidth: '220px' }}
                    title={s.peerDisplayName}
                  >
                    {s.peerDisplayName}
                  </span>
                  <small className="text-muted ms-2">{formatRelativeTime(s.lastMessageAt)}</small>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-1">
                  <small
                    className="text-muted text-truncate"
                    style={{ maxWidth: '260px' }}
                    title={s.lastMessagePreview ?? ''}
                  >
                    {s.lastMessagePreview ?? ''}
                  </small>
                  {s.unreadMessageCount > 0 && (
                    <span className="badge bg-danger rounded-pill ms-2 flex-shrink-0" aria-hidden="true">
                      {s.unreadMessageCount > 99 ? '99+' : s.unreadMessageCount}
                    </span>
                  )}
                </div>
              </Link>
            </li>
          ))
        )}
        <li>
          <div className="dropdown-divider m-0" />
        </li>
        <li>
          <Link to="/mesajlar" className="dropdown-item text-center text-primary fw-semibold py-2">
            Tüm Mesajları Görüntüle
          </Link>
        </li>
      </ul>
    </div>
  );
};

export default MessageCenterDropdown;
